import React, { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Film, TrendingUp, Clock, Loader2, Heart, Search, LogOut, CheckCircle2, Star, X } from 'lucide-react';
import ReleaseCard from './components/ReleaseCard';
import PlatformFilter from './components/PlatformFilter';
import GenreFilter, { matchesAnyGenre, primaryGenreOf } from './components/GenreFilter';
import SearchBar from './components/SearchBar';
import Watchlist from './components/Watchlist';
import Newsletter from './components/Newsletter';
import HeaderNewsletter from './components/HeaderNewsletter';
import TheatricalTrailers from './components/TheatricalTrailers';
import DetailModal from './components/DetailModal';
import SignInPromptModal from './components/SignInPromptModal';
import SubscribePopup from './components/SubscribePopup';
import LatestReviews from './components/LatestReviews';
import AdminQuickBlast from './components/AdminQuickBlast';
import AdminPage from './pages/AdminPage';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [releases, setReleases] = useState([]);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('release_date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [deepLinkRelease, setDeepLinkRelease] = useState(null);
  const [showPhNudge, setShowPhNudge] = useState(() => {
    try {
      return localStorage.getItem('tmj_ph_nudge_dismissed') !== '1';
    } catch {
      return true;
    }
  });
  const { isSubscribed, email: subscribedEmail, signOut, requireSubscription } = useSubscription();

  const dismissPhNudge = () => {
    try {
      localStorage.setItem('tmj_ph_nudge_dismissed', '1');
    } catch {
      // ignore storage failures (private mode / disabled)
    }
    setShowPhNudge(false);
  };

  // Fetch releases
  const fetchReleases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/releases`);
      setReleases(response.data);
      setFilteredReleases(response.data);
      setError(null);
      return response.data;
    } catch (e) {
      console.error('Error fetching releases:', e);
      setError('Failed to load releases. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/releases/stats`);
      setStats(response.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  useEffect(() => {
    const init = async () => {
      const data = await fetchReleases();
      await fetchStats();
      
      // Check for deep link parameters
      const releaseId = searchParams.get('release');
      if (releaseId && data.length > 0) {
        const release = data.find(r => r.id === releaseId);
        if (release) {
          setDeepLinkRelease(release);
        }
      }
    };
    
    init();
    
    // Update watchlist count
    const updateWatchlistCount = () => {
      const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
      setWatchlistCount(watchlist.length);
    };
    
    updateWatchlistCount();
    window.addEventListener('watchlistUpdated', updateWatchlistCount);
    
    return () => {
      window.removeEventListener('watchlistUpdated', updateWatchlistCount);
    };
  }, []);
  
  // Get theatrical deep link ID
  const theatricalDeepLinkId = searchParams.get('theatrical');

  // Filter and search logic
  useEffect(() => {
    let filtered = [...releases];

    // Platform filter
    if (selectedPlatform !== 'All') {
      filtered = filtered.filter(
        (r) => r.platform.toLowerCase() === selectedPlatform.toLowerCase()
      );
    }

    // Multi-genre filter (OR) — shared helper so theatrical stays in sync
    if (selectedGenres.length > 0) {
      filtered = filtered.filter((r) => matchesAnyGenre(r, selectedGenres));
    }

    // Search filter - search across title, genres, and cast
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((r) =>
        r.title.toLowerCase().includes(query) ||
        (r.genres && r.genres.toLowerCase().includes(query)) ||
        (r.cast && r.cast.toLowerCase().includes(query))
      );
    }

    // Sort
    if (sortBy === 'platform') {
      filtered.sort((a, b) => a.platform.localeCompare(b.platform));
    } else if (sortBy === 'genre') {
      // Primary genre asc, then release_date desc as tiebreaker so newest
      // titles rise to the top within each genre bucket.
      filtered.sort((a, b) => {
        const ga = primaryGenreOf(a);
        const gb = primaryGenreOf(b);
        if (ga !== gb) return ga.localeCompare(gb);
        return new Date(b.release_date) - new Date(a.release_date);
      });
    } else {
      filtered.sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
    }

    setFilteredReleases(filtered);
  }, [selectedPlatform, selectedGenres, searchQuery, sortBy, releases]);

  // Categorize releases
  const categorizeReleases = () => {
    const now = new Date();
    const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const thisWeek = [];
    const upcoming = [];
    const recent = [];

    filteredReleases.forEach((release) => {
      const releaseDate = new Date(release.release_date);

      if (releaseDate > now && releaseDate <= weekLater) {
        thisWeek.push(release);
      } else if (releaseDate > weekLater) {
        upcoming.push(release);
      } else if (releaseDate >= monthAgo && releaseDate <= now) {
        recent.push(release);
      }
    });

    return { thisWeek, upcoming, recent };
  };

  const { thisWeek, upcoming, recent } = categorizeReleases();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-black animate-spin mx-auto" />
          <p className="font-secondary text-gray-800">Loading releases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="text-red-500 text-6xl">⚠️</div>
          <h2 className="font-primary text-2xl text-black uppercase">Error</h2>
          <p className="font-secondary text-gray-800">{error}</p>
          <button
            onClick={fetchReleases}
            className="bg-black text-white hover:bg-gray-800 font-secondary font-bold py-3 px-8 rounded-full transition-transform hover:scale-105 active:scale-95"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] text-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between gap-2 h-16">
            <a href="https://themoviejunkie.com" target="_blank" rel="noopener noreferrer" data-testid="header-brand-link" className="min-w-0 flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_7fc7b252-3d64-416a-ba82-e0b493edf6d8/artifacts/s5d6kmh6_TMJ%20with%20R%20MArk.png" 
                alt="The Movie Junkie Logo" 
                className="h-10 md:h-12 w-auto shrink-0"
              />
              <h1 data-testid="header-brand-title" className="min-w-0 font-primary text-base sm:text-lg md:text-xl lg:text-2xl uppercase text-white leading-tight">
                TMJ Streaming Guide
              </h1>
            </a>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3 ml-auto">
              {isSubscribed ? (
                <div
                  className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full bg-green-500/15 border border-green-500/30"
                  data-testid="subscribed-pill"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="font-mono text-xs text-green-300 max-w-[10rem] truncate" title={subscribedEmail}>
                    {subscribedEmail}
                  </span>
                  <button
                    onClick={signOut}
                    data-testid="signout-btn"
                    aria-label="Sign out"
                    title="Sign out"
                    className="p-1 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5 text-green-300" />
                  </button>
                </div>
              ) : (
                <HeaderNewsletter />
              )}
              {isSubscribed && (
                <button
                  onClick={signOut}
                  data-testid="signout-btn-mobile"
                  aria-label="Sign out"
                  title="Sign out"
                  className="sm:hidden p-2 bg-white/10 hover:bg-white/20 rounded-full border border-white/20 transition-all"
                >
                  <LogOut className="w-4 h-4 text-white" />
                </button>
              )}
              {/* AdminQuickBlast self-gates on a valid ADMIN_TOKEN in
                  localStorage (SEC-001) — never rendered unless the admin
                  has signed in via /admin, so no bundle exposure. */}
              <AdminQuickBlast />
              <button
                onClick={() => requireSubscription(() => setShowWatchlist(true), 'watchlist')}
                data-testid="watchlist-button"
                aria-label="Open watchlist"
                className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-all border border-white/20"
              >
                <Heart className={`w-5 h-5 ${watchlistCount > 0 ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                <span className="font-mono text-sm text-white hidden md:inline">
                  Watchlist {watchlistCount > 0 && `(${watchlistCount})`}
                </span>
              </button>
              <div className="hidden lg:flex items-center gap-6 font-mono text-xs uppercase tracking-wider text-gray-300">
                {stats && (
                  <>
                    <span data-testid="stat-upcoming">{stats.upcoming} Upcoming</span>
                    <span data-testid="stat-recent">{stats.recent} Recent</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Hero Section */}
          <div className="mb-12 space-y-8">
            <h2 data-testid="guide-heading" className="font-primary text-[1.4rem] sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-tight text-black break-words">
              The Movie Junkie Streaming Guide
            </h2>
            <p className="font-secondary text-base md:text-lg text-gray-800 leading-relaxed max-w-2xl">
              Discover upcoming and recent releases across Netflix, Prime Video, Apple TV+, Hulu, Disney+, and HBO Max
            </p>
          </div>

          {/* Filters */}
          <div className="mb-12 space-y-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
              <div className="flex gap-3">
                <button
                  data-testid="sort-date"
                  onClick={() => setSortBy('release_date')}
                  className={`px-4 py-2 rounded-full border transition-all text-sm font-mono uppercase ${
                    sortBy === 'release_date'
                      ? 'bg-black text-white border-black'
                      : 'border-gray-700 bg-white text-black hover:border-black'
                  }`}
                >
                  Date
                </button>
                <button
                  data-testid="sort-platform"
                  onClick={() => setSortBy('platform')}
                  className={`px-4 py-2 rounded-full border transition-all text-sm font-mono uppercase ${
                    sortBy === 'platform'
                      ? 'bg-black text-white border-black'
                      : 'border-gray-700 bg-white text-black hover:border-black'
                  }`}
                >
                  Platform
                </button>
                <button
                  data-testid="sort-genre"
                  onClick={() => setSortBy('genre')}
                  className={`px-4 py-2 rounded-full border transition-all text-sm font-mono uppercase ${
                    sortBy === 'genre'
                      ? 'bg-black text-white border-black'
                      : 'border-gray-700 bg-white text-black hover:border-black'
                  }`}
                >
                  Genre
                </button>
              </div>
            </div>
            <PlatformFilter
              selectedPlatform={selectedPlatform}
              onPlatformChange={setSelectedPlatform}
            />
            <GenreFilter
              selectedGenres={selectedGenres}
              onToggleGenre={(name) =>
                setSelectedGenres((prev) =>
                  prev.includes(name)
                    ? prev.filter((g) => g !== name)
                    : [...prev, name]
                )
              }
              onClearGenres={() => setSelectedGenres([])}
            />
          </div>

          {/* Search Results Section - Shows when there's an active search */}
          {searchQuery && filteredReleases.length > 0 && (
            <section className="mb-16" data-testid="search-results-section">
              <div className="flex items-center gap-3 mb-6">
                <Search className="w-6 h-6 text-black" />
                <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
                  Search Results ({filteredReleases.length})
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8">
                {filteredReleases.map((release) => (
                  <ReleaseCard key={release.id} release={release} />
                ))}
              </div>
            </section>
          )}

          {/* This Week Section - Only show when NOT searching */}
          {!searchQuery && thisWeek.length > 0 && (
            <section className="mb-16" data-testid="this-week-section">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-black" />
                <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
                  This Week
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8">
                {thisWeek.map((release) => (
                  <ReleaseCard key={release.id} release={release} />
                ))}
              </div>
            </section>
          )}

          {/* Upcoming Releases Section - Only show when NOT searching */}
          {!searchQuery && upcoming.length > 0 && (
            <section className="mb-16" data-testid="upcoming-section">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-black" />
                <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
                  Upcoming Releases
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8">
                {upcoming.slice(0, 10).map((release) => (
                  <ReleaseCard key={release.id} release={release} />
                ))}
              </div>
            </section>
          )}

          {/* Recently Released Section - Only show when NOT searching */}
          {!searchQuery && recent.length > 0 && (
            <section className="mb-16" data-testid="recent-section">
              <div className="flex items-center gap-3 mb-6">
                <Film className="w-6 h-6 text-black" />
                <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
                  Recently Released
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8">
                {recent.slice(0, 10).map((release) => (
                  <ReleaseCard key={release.id} release={release} />
                ))}
              </div>
            </section>
          )}

          {/* Latest Reviews from themoviejunkie.com — soft-hides on empty */}
          {!searchQuery && <LatestReviews />}

          {/* The Movie Junkie Reviews Section - Always between OTT picks and Theatrical */}
          {!searchQuery && (
          <section className="mb-16 text-center" data-testid="reviews-section">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-black max-w-4xl mx-auto">
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_7fc7b252-3d64-416a-ba82-e0b493edf6d8/artifacts/s5d6kmh6_TMJ%20with%20R%20MArk.png"
                    alt="The Movie Junkie"
                    className="h-24 md:h-30 w-auto"
                  />
                </div>
                <h2 className="font-primary text-3xl md:text-5xl uppercase tracking-wide text-black">
                  Looking for Reviews?
                </h2>
                <p className="font-secondary text-lg md:text-xl text-gray-800 max-w-2xl mx-auto leading-relaxed">
                  Check out in-depth movie and series reviews, ratings, and recommendations for all your favorites!
                </p>
                <a
                  href="https://themoviejunkie.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reviews-link"
                  className="inline-block bg-black text-white hover:bg-gray-800 font-primary text-xl md:text-2xl uppercase py-4 px-12 rounded-full transition-all transform hover:scale-105 active:scale-95 shadow-xl"
                >
                  TheMovieJunkie.com Reviews →
                </a>
              </div>
            </div>
          </section>
          )}

          {/* Theatrical Trailers Section - Only show when NOT searching */}
          {!searchQuery && (
            <TheatricalTrailers 
              deepLinkId={theatricalDeepLinkId} 
              onClearDeepLink={() => setSearchParams({})}
              selectedGenres={selectedGenres}
            />
          )}

          {/* Newsletter Signup - Only show when NOT searching */}
          {!searchQuery && <Newsletter />}

          {/* No results */}
          {filteredReleases.length === 0 && (
            <div className="text-center py-16" data-testid="no-results">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="font-primary text-2xl uppercase mb-2 text-black">No Releases Found</h3>
              <p className="font-secondary text-gray-700">
                Try adjusting your filters or search query
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-700 py-8 bg-black/80">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col items-center gap-5 text-center">
          <a
            href="https://www.producthunt.com/products/upcoming-ott-and-theatrical-releases?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-upcoming-ott-and-theatrical-releases"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="product-hunt-badge"
            className="inline-block transition-transform hover:scale-105 active:scale-95"
          >
            <img
              alt="Upcoming OTT and Theatrical Releases - Stay on top of New OTT and Theatrical Releases | Product Hunt"
              width="250"
              height="54"
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1102293&theme=light&t=1777630535808"
            />
          </a>

          {showPhNudge && (
            <div
              data-testid="ph-review-nudge"
              className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-full pl-4 pr-2 py-2 animate-fade-in"
            >
              <Star className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" />
              <span className="font-secondary text-xs md:text-sm text-gray-200">
                Enjoying this?{' '}
                <a
                  href="https://www.producthunt.com/products/upcoming-ott-and-theatrical-releases/reviews?utm_source=badge-review-nudge&utm_medium=badge&utm_campaign=badge-upcoming-ott-and-theatrical-releases"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ph-review-nudge-link"
                  className="underline decoration-dotted underline-offset-4 hover:text-amber-300 text-amber-200 font-medium"
                >
                  Leave us a review on Product Hunt
                </a>
              </span>
              <button
                onClick={dismissPhNudge}
                data-testid="ph-review-nudge-dismiss"
                aria-label="Dismiss review nudge"
                className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}

          <p className="font-secondary text-sm text-gray-400">
            The Movie Junkie Streaming Guide<sup>©</sup> 2026 • Your complete streaming release calendar
          </p>
        </div>
      </footer>
      
      {/* Watchlist Modal */}
      {showWatchlist && (
        <Watchlist onClose={() => setShowWatchlist(false)} />
      )}
      
      {/* Deep Link Modal for shared releases */}
      {deepLinkRelease && (
        <DetailModal
          release={deepLinkRelease}
          onClose={() => {
            setDeepLinkRelease(null);
            setSearchParams({});
          }}
          isInWatchlist={JSON.parse(localStorage.getItem('watchlist') || '[]').some(w => w.id === deepLinkRelease.id)}
          onToggleWatchlist={() => {
            requireSubscription(() => {
              const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
              const isInList = watchlist.some(w => w.id === deepLinkRelease.id);
              if (isInList) {
                const updated = watchlist.filter(w => w.id !== deepLinkRelease.id);
                localStorage.setItem('watchlist', JSON.stringify(updated));
              } else {
                watchlist.push(deepLinkRelease);
                localStorage.setItem('watchlist', JSON.stringify(watchlist));
              }
              window.dispatchEvent(new Event('watchlistUpdated'));
            }, 'watchlist');
          }}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <SubscriptionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <SignInPromptModal />
      <SubscribePopup />
      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: '#0b0b0b',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
    </SubscriptionProvider>
  );
}

export default App;
